import type { McsrBulkProject } from '../types/mcsr';

/**
 * MCSR bulk print and export: the dialog's selection rules. A parent project (SAP Parent Project empty or itself)
 * brings its children along when ticked, unless "Parent projects only" is on, which hides the children and leaves
 * them out. Only projects with an MCSR in the chosen month can be ticked.
 */

export interface McsrBulkFilter {
  query: string;
  division: string;
  department: string;
  parentsOnly: boolean;
}

const norm = (v: unknown): string => String(v ?? '').trim().toUpperCase();

/** The project passes the filters: every word typed is in its code, name or customer (any case and order). */
export function projectMatches(p: McsrBulkProject, f: McsrBulkFilter): boolean {
  if (f.parentsOnly && p.parent) return false;
  if (f.division && norm(p.division) !== norm(f.division)) return false;
  if (f.department && norm(p.department) !== norm(f.department)) return false;
  const words = String(f.query || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const text = [p.project, p.projectName, p.customerCode, p.customerName].filter(Boolean).join(' ').toLowerCase();
  return words.every((w) => text.includes(w));
}

/** The children of a project in the list (their Parent Project names it). */
export function childrenOf(code: string, projects: McsrBulkProject[]): McsrBulkProject[] {
  return projects.filter((p) => p.parent && norm(p.parent) === norm(code));
}

/** Parents by code, each followed by its children; `child` = shown under its parent. */
export function familyRows(projects: McsrBulkProject[]): Array<{ p: McsrBulkProject; child: boolean }> {
  const codes = new Set(projects.map((p) => norm(p.project)));
  const keyed = projects.map((p) => {
    const under = !!p.parent && codes.has(norm(p.parent));
    return { p, child: under, key: `${under ? norm(p.parent) : norm(p.project)}\u0000${under ? 1 : 0}\u0000${norm(p.project)}` };
  });
  keyed.sort((a, b) => a.key.localeCompare(b.key));
  return keyed.map(({ p, child }) => ({ p, child }));
}

/** Ticks or unticks a project; a parent brings its children (with an MCSR) unless "Parent projects only". */
export function toggleProject(selected: Set<string>, p: McsrBulkProject, all: McsrBulkProject[], parentsOnly: boolean): Set<string> {
  const next = new Set(selected);
  const on = !next.has(p.project);
  const family = [p, ...(!parentsOnly && !p.parent ? childrenOf(p.project, all) : [])].filter((x) => x.hasReport);
  for (const x of family) {
    if (on) next.add(x.project);
    else next.delete(x.project);
  }
  return next;
}

/** Ticks every project shown that has an MCSR; unticks them all when they already are. */
export function toggleAllShown(selected: Set<string>, shown: McsrBulkProject[]): Set<string> {
  const pickable = shown.filter((p) => p.hasReport);
  const next = new Set(selected);
  const allOn = pickable.length > 0 && pickable.every((p) => next.has(p.project));
  for (const p of pickable) {
    if (allOn) next.delete(p.project);
    else next.add(p.project);
  }
  return next;
}

/** "Parent projects only" leaves the children out of the selection. */
export function dropChildren(selected: Set<string>, all: McsrBulkProject[]): Set<string> {
  const children = new Set(all.filter((p) => p.parent).map((p) => p.project));
  return new Set(Array.from(selected).filter((code) => !children.has(code)));
}

/** The divisions or departments the projects carry, for the filters (code and name, by code). */
export function codeOptions(projects: McsrBulkProject[], kind: 'division' | 'department'): Array<{ code: string; name: string }> {
  const out = new Map<string, string>();
  for (const p of projects) {
    const code = kind === 'division' ? p.division : p.department;
    const name = kind === 'division' ? p.divisionName : p.departmentName;
    if (code && !out.has(code)) out.set(code, name || '');
  }
  return Array.from(out.entries()).map(([code, name]) => ({ code, name })).sort((a, b) => a.code.localeCompare(b.code));
}

/** The selection in print order (as the run will print it). */
export function selectionInOrder(selected: Set<string>, all: McsrBulkProject[]): string[] {
  return familyRows(all.filter((p) => selected.has(p.project))).map((r) => r.p.project);
}
